import * as Sentry from '@sentry/react';
import routes from 'agent-landing-routes';
import styles from 'assets/jss/material-dashboard-pro-react/layouts/agentLandingStyle';
import qs from 'qs';
import React from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { agentLandingPageSampleTokens, EAgentLandingPageType } from 'types/agent-landing-page';

import 'components/AgentLanding/agent-landing.css';

// import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import AgentLandingFooter from 'components/AgentLanding/AgentLandingFooter/AgentLandingFooter';
import AgentLandingNavbar from 'components/AgentLanding/AgentLandingNavbar/AgentLandingNavbar';
import ContactModal from 'components/AgentLanding/Contact/ContactModal';
import PublicRoute from 'components/AuthDataProvider/PublicRoute';
// import LoadingDots from 'components/LoadingDots/LoadingDots';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import { useModalContext } from 'components/ModalProvider/ModalProvider';

import AgentLandingErrorPage from 'views/AgentLanding/AgentLandingErrorPage';

import { getAgentLandingHomeEstimationData, getAgentLandingPageData, getAgentLandingSampleData } from 'utils/api';
import { isAgentLandingPageSample } from 'utils/lib';

const useStyles = makeStyles(styles);

const AgentLanding = ({ location }) => {
  const classes = useStyles();
  const history = useHistory();
  const { currentModal } = useModalContext();
  const { isLoadingIndicatorModalShowing } = useLoadingIndicatorContext();

  const { token: agentLandingPageToken, subject: eventId } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [agentLandingPageType, setAgentLandingPageType] = React.useState(null);
  const [agentData, setAgentData] = React.useState(null);
  const [marketAreaName, setMarketAreaName] = React.useState(null);

  /* For 'COMPARABLE_PROPERTIES' type */
  const [propertyData, setPropertyData] = React.useState(null);
  const [similarHomes, setSimilarHomes] = React.useState(null);
  /* For 'COMPARABLE_PROPERTIES' type */

  const redirectToErrorPage = () => history.push('/error');

  const captureAgentLandingRequestError = (err, endpoint) => {
    Sentry.withScope((scope) => {
      scope.setTag('agentLandingEndpoint', endpoint);
      scope.setContext('agentLandingRequest', {
        endpoint,
        status: err.response?.status,
        responseError: err.response?.data?.error,
      });

      Sentry.captureException(new Error(`Unable to get agent landing ${endpoint} data with agentLandingPageToken ${agentLandingPageToken}`));
    });
  };

  // Fetch home estimation data
  React.useEffect(() => {
    (async () => {
      if (!agentLandingPageToken) {
        redirectToErrorPage();
        return;
      }

      try {
        // If this is a sample page, get some example data to work with
        if (isAgentLandingPageSample(agentLandingPageToken)) {
          const data = await getAgentLandingSampleData(agentLandingPageToken, eventId);

          setAgentLandingPageType(data.agentLandingPageType);
          setMarketAreaName(data.marketAreaName);
          setAgentData(data.agentData);

          if (data.propertyData && data.similarHomes) {
            setPropertyData(data.propertyData);
            setSimilarHomes(data.similarHomes);
          } else if (agentLandingPageToken === agentLandingPageSampleTokens[EAgentLandingPageType.ComparableProperties]) {
            // If no comparable properties are found we fall back to the CONTACT_FORM agent landing page type
            setAgentLandingPageType(EAgentLandingPageType.ContactForm);
          }

          return;
        }

        let landingPageData;

        try {
          landingPageData = await getAgentLandingPageData(agentLandingPageToken);
        } catch (err) {
          captureAgentLandingRequestError(err, 'page-data');

          console.error(err);
          redirectToErrorPage();
          return;
        }

        setAgentLandingPageType(landingPageData.agentLandingPageType);
        setMarketAreaName(landingPageData.marketAreaName);
        setAgentData(landingPageData.agentData);

        if (landingPageData.agentLandingPageType === EAgentLandingPageType.ComparableProperties) {
          let homeEstimationData;

          try {
            homeEstimationData = await getAgentLandingHomeEstimationData(agentLandingPageToken);
          } catch (err) {
            // If the subject property can't be searched by benutech we fall back to the CONTACT_FORM agent landing page type
            if (err.response?.data?.error === 'Unable to get property details') {
              setAgentLandingPageType(EAgentLandingPageType.ContactForm);
              return;
            }

            captureAgentLandingRequestError(err, 'home-estimation');

            console.error(err);
            redirectToErrorPage();
            return;
          }

          setPropertyData(homeEstimationData.propertyData);
          setSimilarHomes(homeEstimationData.similarHomes || []);
        }
      } catch (err) {
        captureAgentLandingRequestError(err, 'sample-data');

        console.error(err);
        redirectToErrorPage();
      }
    })();
  }, []);

  const [assessmentModalOpen, setAssessmentModalOpen] = React.useState(false);
  // const [isDownloadingReport, setIsDownloadingReport] = React.useState(false);
  const isComparableDataLoading = agentLandingPageType === EAgentLandingPageType.ComparableProperties
    && (propertyData === null || similarHomes === null);

  const getRoutes = (routes2) => routes2.map((prop, key) => {
    if (prop.collapse) {
      return getRoutes(prop.views);
    }

    const RenderComponent = prop.render || prop.component;

    return (
      <Route
        exact={prop.exact}
        path={prop.layout + prop.path}
        render={() => (
          // Keep the loading state until both agent data and any required property data are resolved.
          agentData && !isComparableDataLoading ? (
            <RenderComponent
              agentLandingPageType={agentLandingPageType}
              agentLandingPageToken={agentLandingPageToken}
              agentData={agentData}
              propertyData={propertyData}
              similarHomes={similarHomes}
              marketAreaName={marketAreaName}
              // setIsDownloadingReport={setIsDownloadingReport}
              // eventId={eventId}
            />
          ) : (<LoadingIndicator modal />)
        )}
        key={key}
      />
    );
  });

  return (
    <div className={`${classes.wrapper} agent-landing`}>
      <AgentLandingNavbar
        agentLandingPageType={agentLandingPageType}
        agentLandingPageToken={agentLandingPageToken}
        agentData={agentData}
        openAssessmentModal={() => setAssessmentModalOpen(true)}
      />

      {currentModal}
      {isLoadingIndicatorModalShowing ? (<LoadingIndicator modal />) : null}

      <div className={classes.content}>
        {/* Download-report dialog retained for future use.
        {isDownloadingReport && (
          <Dialog
            classes={{
              root: classes.downloadingReportModalRoot,
              paper: classes.downloadingReportModalPaper,
            }}
            open
            keepMounted
            maxWidth="sm"
          >
            <h3>Downloading Report</h3>
            <p>Your report is being generated, please wait...</p>
            <LoadingDots />
          </Dialog>
        )}
        */}

        {assessmentModalOpen && <ContactModal onClose={() => setAssessmentModalOpen(false)} agentLandingPageToken={agentLandingPageToken} agentData={agentData} variant="report" />}

        <Switch>
          {getRoutes(routes)}
          <Route path="*" component={() => (<PublicRoute component={AgentLandingErrorPage} />)} />
        </Switch>
      </div>
      {agentData && <AgentLandingFooter agentData={agentData} />}
    </div>
  );
};

export default AgentLanding;
